// RC10s · "Driver Cannot Be Assigned" dialog.
//
// Renders every blocking_reason as its own labeled card with the
// Owner-briefed action buttons:
//   * View Driver           — DRIVER_* / credential codes
//   * View Availability     — DRIVER_EXPLICITLY_UNAVAILABLE
//   * View Conflicting Tour — DRIVER_ALREADY_ASSIGNED
//   * Find Replacement      — DRIVER_INACTIVE / DRIVER_ARCHIVED /
//                             CDL_EXPIRED_BEFORE_TOUR / MEDICAL_* /
//                             DRIVER_RESTRICTED_FOR_CUSTOMER /
//                             DRIVER_EXPLICITLY_UNAVAILABLE /
//                             DRIVER_ALREADY_ASSIGNED
//   * Update Credential     — CDL_EXPIRED_BEFORE_TOUR / MEDICAL_*
//   * Cancel                — always
//
// Blocking cards use critical-red border + chip. Warning cards (if
// surfaced alongside) use amber-hold.
//
// Mobile + desktop responsive. Preserves user driver selection via
// the onFindReplacement callback (parent decides whether to reset).
import React from "react";
import { useNavigate } from "react-router-dom";
import { X, AlertTriangle, Info } from "lucide-react";

const BLOCK_CHIP_LABEL = {
  DRIVER_NOT_FOUND: "Driver Not Found",
  DRIVER_ARCHIVED: "Driver Archived",
  DRIVER_INACTIVE: "Driver Inactive",
  DRIVER_EXPLICITLY_UNAVAILABLE: "Explicitly Unavailable",
  DRIVER_ALREADY_ASSIGNED: "Already Assigned",
  DUPLICATE_TOUR_ASSIGNMENT: "Duplicate Assignment",
  CDL_EXPIRED_BEFORE_TOUR: "CDL Expired",
  MEDICAL_CARD_EXPIRED_BEFORE_TOUR: "Medical Card Expired",
  REQUIRED_DQ_ITEM_MISSING: "DQ Item Missing",
  DRIVER_RESTRICTED_FOR_CUSTOMER: "DO NOT ASSIGN",
  TOUR_NOT_FOUND: "Tour Not Found",
  TOUR_ARCHIVED: "Tour Archived",
  TOUR_STATUS_NOT_ASSIGNABLE: "Tour Not Assignable",
  INVALID_TOUR_DATES: "Invalid Tour Dates",
  INVALID_DRIVER_ID: "Invalid Driver",
  INVALID_ASSIGNMENT_ROLE: "Invalid Role",
  ASSIGNMENT_PERMISSION_DENIED: "Permission Denied",
  CROSS_TENANT_REFERENCE: "Access Denied",
  CONCURRENT_ASSIGNMENT_CHANGE: "Records Changed",
  UNEXPECTED_ASSIGNMENT_ERROR: "Unexpected Error",
};

const DRIVER_ACTIONS = new Set([
  "DRIVER_ARCHIVED", "DRIVER_INACTIVE",
  "CDL_EXPIRED_BEFORE_TOUR", "MEDICAL_CARD_EXPIRED_BEFORE_TOUR",
  "REQUIRED_DQ_ITEM_MISSING", "DRIVER_RESTRICTED_FOR_CUSTOMER",
  "DRIVER_EXPLICITLY_UNAVAILABLE", "DRIVER_ALREADY_ASSIGNED",
]);
const CREDENTIAL_ACTIONS = new Set([
  "CDL_EXPIRED_BEFORE_TOUR", "MEDICAL_CARD_EXPIRED_BEFORE_TOUR",
  "REQUIRED_DQ_ITEM_MISSING",
]);
const REPLACEMENT_ACTIONS = new Set([
  "DRIVER_ARCHIVED", "DRIVER_INACTIVE",
  "CDL_EXPIRED_BEFORE_TOUR", "MEDICAL_CARD_EXPIRED_BEFORE_TOUR",
  "DRIVER_RESTRICTED_FOR_CUSTOMER", "DRIVER_EXPLICITLY_UNAVAILABLE",
  "DRIVER_ALREADY_ASSIGNED",
]);

function BlockingReasonCard({
  reason, driverId, tourId, onFindReplacement, onNavigate, onClose,
}) {
  const code = reason.code;
  const label = BLOCK_CHIP_LABEL[code] || "Cannot Assign";
  const details = reason.details || {};
  const showViewDriver = DRIVER_ACTIONS.has(code) && driverId;
  const showAvailability = code === "DRIVER_EXPLICITLY_UNAVAILABLE" && driverId;
  const showConflict = code === "DRIVER_ALREADY_ASSIGNED" && details.conflicting_tour_id;
  const showReplacement = REPLACEMENT_ACTIONS.has(code);
  const showCredential = CREDENTIAL_ACTIONS.has(code) && driverId;

  return (
    <div
      data-testid={`rc10s-block-reason-${code}`}
      className="border border-critical-red/60 rounded-md bg-white p-3 flex gap-3"
    >
      <div className="shrink-0 pt-0.5">
        <AlertTriangle size={18} className="text-critical-red" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span
            className="inline-block text-[10px] uppercase tracking-widest font-medium bg-critical-red/10 text-critical-red px-2 py-0.5 rounded-sm"
            data-testid={`rc10s-block-chip-${code}`}
          >
            {label}
          </span>
          <span className="text-[10px] uppercase tracking-widest text-steel">
            {code}
          </span>
        </div>
        <div
          className="mt-1.5 text-[13px] text-carbon leading-snug"
          data-testid={`rc10s-block-message-${code}`}
        >
          {reason.message}
        </div>
        {reason.resolution && (
          <div
            className="mt-1 text-[12px] text-steel leading-snug"
            data-testid={`rc10s-block-resolution-${code}`}
          >
            {reason.resolution}
          </div>
        )}
        <div className="mt-2 flex flex-wrap gap-2">
          {showViewDriver && (
            <button
              type="button"
              onClick={() => onNavigate(`/drivers/${driverId}`)}
              data-testid={`rc10s-action-view-driver-${code}`}
              className="text-[12px] px-2.5 py-1 border border-coach-silver rounded-sm bg-white hover:bg-coach-silver/20 text-carbon"
            >
              View Driver
            </button>
          )}
          {showAvailability && (
            <button
              type="button"
              onClick={() => onNavigate(`/drivers/${driverId}?tab=availability`)}
              data-testid={`rc10s-action-view-availability-${code}`}
              className="text-[12px] px-2.5 py-1 border border-coach-silver rounded-sm bg-white hover:bg-coach-silver/20 text-carbon"
            >
              View Availability
            </button>
          )}
          {showConflict && (
            <button
              type="button"
              onClick={() => onNavigate(`/tours/${details.conflicting_tour_id}`)}
              data-testid={`rc10s-action-view-conflict-${code}`}
              className="text-[12px] px-2.5 py-1 border border-coach-silver rounded-sm bg-white hover:bg-coach-silver/20 text-carbon"
            >
              View Conflicting Tour
            </button>
          )}
          {showCredential && (
            <button
              type="button"
              onClick={() => onNavigate(`/drivers/${driverId}?tab=credentials`)}
              data-testid={`rc10s-action-update-credential-${code}`}
              className="text-[12px] px-2.5 py-1 border border-coach-blue text-coach-blue rounded-sm bg-white hover:bg-coach-blue/5 font-medium"
            >
              Update Credential
            </button>
          )}
          {showReplacement && onFindReplacement && (
            <button
              type="button"
              onClick={onFindReplacement}
              data-testid={`rc10s-action-find-replacement-${code}`}
              className="text-[12px] px-2.5 py-1 border border-coach-silver rounded-sm bg-white hover:bg-coach-silver/20 text-carbon"
            >
              Find Replacement
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function WarningReasonCard({ reason, onAcknowledge, canAcknowledge }) {
  const code = reason.code;
  return (
    <div
      data-testid={`rc10s-warning-reason-${code}`}
      className="border border-amber-hold/70 rounded-md bg-amber-hold/5 p-3 flex gap-3"
    >
      <div className="shrink-0 pt-0.5">
        <Info size={18} className="text-amber-hold" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[10px] uppercase tracking-widest font-medium text-amber-hold">
          Warning · {code}
        </div>
        <div
          className="mt-1 text-[13px] text-carbon leading-snug"
          data-testid={`rc10s-warning-message-${code}`}
        >
          {reason.message}
        </div>
        {reason.resolution && (
          <div className="mt-1 text-[12px] text-steel">{reason.resolution}</div>
        )}
        {canAcknowledge && onAcknowledge && (
          <div className="mt-2">
            <button
              type="button"
              onClick={onAcknowledge}
              data-testid={`rc10s-action-ack-${code}`}
              className="text-[12px] px-2.5 py-1 border border-amber-hold text-amber-hold rounded-sm bg-white hover:bg-amber-hold/10 font-medium"
            >
              Acknowledge &amp; Continue
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function DriverCannotBeAssignedDialog({
  open,
  diagnostic,          // {eligible, blocking_reasons[], warnings[], correlation_id}
  driverId,
  tourId,
  driverName,
  onClose,
  onFindReplacement,   // optional — invoked by "Find Replacement" button
  onAcknowledgeWarning,// optional — invoked when a warning offers ack
}) {
  const navigate = useNavigate();
  if (!open || !diagnostic) return null;
  const blocks = diagnostic.blocking_reasons || [];
  const warns = diagnostic.warnings || [];
  const hasBlocks = blocks.length > 0;
  const handleNavigate = (path) => {
    if (onClose) onClose();
    navigate(path);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-carbon/60 flex items-start justify-center overflow-y-auto p-3 md:p-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="rc10s-dialog-title"
      data-testid="rc10s-driver-cannot-be-assigned-dialog"
      onClick={onClose}
    >
      <div
        className="bg-road-white rounded-md shadow-lg w-full max-w-2xl border border-coach-silver"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="bg-carbon text-white px-4 py-3 flex items-center justify-between rounded-t-md">
          <div className="min-w-0">
            <div id="rc10s-dialog-title" className="text-[14px] font-medium">
              {hasBlocks ? "Driver Cannot Be Assigned" : "Driver Assignment Warning"}
            </div>
            {driverName && (
              <div className="text-[11px] text-coach-silver truncate">
                {driverName}
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="rc10s-dialog-close"
            aria-label="Close"
            className="text-coach-silver hover:text-white shrink-0"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-4 space-y-3">
          {hasBlocks && (
            <div className="text-[12px] text-steel">
              {blocks.length === 1
                ? "The following issue prevents this assignment:"
                : `The following ${blocks.length} issues prevent this assignment:`}
            </div>
          )}
          {blocks.map((r, idx) => (
            <BlockingReasonCard
              key={`${r.code}-${idx}`}
              reason={r}
              driverId={driverId}
              tourId={tourId}
              onFindReplacement={onFindReplacement}
              onNavigate={handleNavigate}
              onClose={onClose}
            />
          ))}
          {warns.length > 0 && !hasBlocks && (
            <div className="text-[12px] text-steel">
              Acknowledge the following warning to proceed with the assignment:
            </div>
          )}
          {warns.map((r, idx) => (
            <WarningReasonCard
              key={`w-${r.code}-${idx}`}
              reason={r}
              onAcknowledge={!hasBlocks ? onAcknowledgeWarning : undefined}
              canAcknowledge={!hasBlocks}
            />
          ))}
          <div className="pt-2 flex items-center justify-between gap-3">
            <div className="text-[10px] text-steel truncate">
              Diagnostic ID: <span data-testid="rc10s-correlation-id">{diagnostic.correlation_id || "—"}</span>
            </div>
            <button
              type="button"
              onClick={onClose}
              data-testid="rc10s-dialog-cancel"
              className="text-[12.5px] px-3 py-1.5 border border-coach-silver rounded-sm bg-white hover:bg-coach-silver/20 text-carbon"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
